import React, {useEffect, useMemo, useState} from "react";
import {Routes, Route, useLocation} from "react-router-dom";
import {SVVStepIndicator} from "@komponentkassen/svv-step-indicator";
import RebookPreview from "./RebookPreview";
import Service from "../shared/Service";
import Section from "../shared/Section";
import DateAndTime from "../shared/DateAndTime";
import ContactInfo from "../shared/ContactInfo";
import Confirmation from "../shared/Confirmation";
import GetBooking from "../shared/GetBooking";
import Receipt from "../shared/Receipt";
import trimStegviser from "../../helpers/TrimStegviser";
import InterfaceBooking from "../../interface/InterfaceBooking";
import InterfaceService from "../../interface/InterfaceService";
import InterfaceSection from "../../interface/InterfaceSection";
import InterfaceContactInfo from "../../interface/InterfaceContactInfo";
import EMPTY_BOOKING from "../../helpers/EmptyBooking";
import EMPTY_CONTACT_INFO from "../../helpers/EmptyContactInfo";
import api from "../../helpers/api";
import {EMPTY_SECTION} from "../../helpers/EmptySection";
import {useApptekster} from "../../apptekst-context";
import {ReminderString} from "../../helpers/SharedTypes";
import {SVVLayoutBanner, SVVLayoutPageType, SVVLayoutPageWrapper} from "@komponentkassen/svv-layout";
import {SVVHeading, SVVIngress} from "@komponentkassen/svv-text";
import {TjenesteHeading} from "../shared/TjenesteHeading";

enum RebookUrls {
  GET_BOOKING = "/endre/endretime",
  CONFIRM = "/endre/bekrefte",
  SERVICE = "/endre/tjeneste",
  SECTION = "/endre/trafikkstasjon",
  DATE_AND_TIME = "/endre/tidspunkt",
  CONTACT = "/endre/kontakt",
  CONFIRMATION = "/endre/bekreftetime",
  RECEIPT = "/endre/kvittering",
}

const Rebook = ({avbrytUrl}: {avbrytUrl?: string}) => {
  const t = useApptekster();
  const [originalBooking, setOriginalBooking] = useState<InterfaceBooking>(EMPTY_BOOKING);
  //Ny booking med ny bookingId og nytt bookingNumber fra backend ved rebook, basert på nye inputverdiene fra bruker
  const [newBooking, setNewBooking] = useState<InterfaceBooking>(EMPTY_BOOKING);
  const [contactInfo, setContactInfo] = useState<InterfaceContactInfo>(EMPTY_CONTACT_INFO);
  const [reserved, setReserved] = useState<boolean>(false);
  const [navState, setNavState] = useState<number>(-1);
  const [dateAndTime, setDateAndTime] = useState('');
  const [service, setService] = useState<InterfaceService>({serviceId: 0, serviceName: '', isTungKjoreToy: false});
  const [section, setSection] = useState<InterfaceSection>({sectionId: 0, sectionName: ''});
  const [sendReminderTo, setSendReminderTo] = useState<ReminderString>("both")
  const [routes, setRoutes] = useState([
    {
      href: RebookUrls.SECTION,
      name: t("timebestilling.trafikkstasjon"),
      active: false,
    },
    {
      href: RebookUrls.DATE_AND_TIME,
      name: t("timebestilling.tidspunkt"),
      active: false,
    },
    {
      href: RebookUrls.CONTACT,
      name: t("timebestilling.kontaktinformasjon"),
      active: false,
    },
    {
      href: RebookUrls.CONFIRMATION,
      name: t("timebestilling.oppsummering"),
      active: false,
    }
  ])
  const location = useLocation();
  // const [maxNavState, setMaxNavState] = useState(-1)
  
  const alleRoutes = [
    {
      href: RebookUrls.SERVICE,
      name: t("timebestilling.velg-tjeneste"),
      active: false,
    },
    {
      href: RebookUrls.SECTION,
      name: t("timebestilling.trafikkstasjon"),
      active: false,
    },
    {
      href: RebookUrls.DATE_AND_TIME,
      name: t("timebestilling.tidspunkt"),
      active: false,
    },
    {
      href: RebookUrls.CONTACT,
      name: t("timebestilling.kontaktinformasjon"),
      active: false,
    },
    {
      href: RebookUrls.CONFIRMATION,
      name: t("timebestilling.oppsummering"),
      active: false,
    }
  ]
  
  const {bannerHeading, bannerIngress} = useMemo(() => {
    //TODO: Fikse sånn at når location endrer seg -> sjekke om data for det steget finnes
    let bannerHeading = t("timebestilling.endre.hent-time-overskrift")
    let bannerIngress = t("timebestilling.endre.hent-time-ingress")

    if (location?.pathname) {
      switch (location.pathname) {
        case RebookUrls.SERVICE:
          bannerHeading = t("timebestilling.bestill.tjeneste-overskrift")
          bannerIngress = t("timebestilling.bestill.tjeneste-ingress")
          break
        case RebookUrls.SECTION:
          bannerHeading = t("timebestilling.bestill.trafikkstasjon-overskrift")
          bannerIngress = t("timebestilling.bestill.trafikkstasjon-ingress")
          break
        case RebookUrls.DATE_AND_TIME:
          bannerHeading = t("timebestilling.bestill.tidspunkt-overskrift")
          bannerIngress = t("timebestilling.bestill.tidspunkt-ingress")
          break
        case RebookUrls.CONTACT:
          bannerHeading = t("timebestilling.bestill.kontakt-overskrift")
          bannerIngress = t("timebestilling.bestill.kontakt-ingress")
          break
        case RebookUrls.CONFIRMATION:
          bannerHeading = t("timebestilling.bestill.fullfor-overskrift")
          bannerIngress = t("timebestilling.bestill.fullfor-ingress")
          break
        case RebookUrls.RECEIPT:
          bannerHeading = t("timebestilling.bestill.bekreftelse-overskrift")
          bannerIngress = t("timebestilling.bestill.bekreftelse-ingress")
          break
      }
    }
    return {
      bannerHeading,
      bannerIngress
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location])

  const resetBookingIdAndDateTime = () => {
    setReserved(false)
    setDateAndTime('')
    setNavState(routes.findIndex((r) => r.href === RebookUrls.DATE_AND_TIME))
  }
  
  const resetBookingSectionAndDateTime = () => {
    setReserved(false)
    setDateAndTime('')
    setSection(EMPTY_SECTION)
    setNavState(routes.findIndex((r) => r.href === RebookUrls.SECTION))
  }

  function abort() {
    if (reserved && originalBooking.bookingId) {
      api
        .get(
          `/api/Timebestilling/booking/unreserve?bookingId=${originalBooking.bookingId}`
        )
        .catch((error) => {
          // handle error
          console.log(error);
        })
        .finally(() => {
          setOriginalBooking(EMPTY_BOOKING);
          setReserved(false);
        });
    }
    if (avbrytUrl) {
      window.location.href = avbrytUrl;
    }
  }

  const visVelgTjeneste = useMemo(() => {
    return !!(originalBooking.serviceName && originalBooking.serviceName === "Teori")
  }, [originalBooking]);

  useEffect(() => {
    const {
      sectionId,
      sectionName,
      bookedDateTime,
      bookingId,
    } = originalBooking
    
    if (sectionId && sectionName) {
      setSection({sectionId: sectionId, sectionName: sectionName})
    }
    if (bookedDateTime) {
      setDateAndTime(bookedDateTime)
    }
    if (bookingId) {
      setReserved(true)
    }
    if (!visVelgTjeneste) {
      setService({
        serviceId: originalBooking.serviceId ,
        serviceName: originalBooking.serviceName,
        isTungKjoreToy: false
      })
    }
    // eslint-disable-next-line
  }, [originalBooking])

  useEffect(() => {
    if (!visVelgTjeneste) {
      setService({
        serviceId: originalBooking.serviceId ,
        serviceName: originalBooking.serviceName,
        isTungKjoreToy: false
      })
    } else {
      setRoutes(alleRoutes)
      //Hvis visVelgTjeneste betyr det at tjenesten er teori, default til tung teori slik
      //at første radioknapp er valgt i <Service>
      setService({
        serviceId: originalBooking.serviceId ,
        serviceName: originalBooking.serviceName,
        isTungKjoreToy: true
      })
    }
    // eslint-disable-next-line
  }, [visVelgTjeneste]);

  useEffect(() => {
    if (routes.length > 0) {
      setNavState(routes.length)
    }
  }, [routes]);
  
  return (
    <>
      <SVVLayoutBanner 
        medium
        noPaddingTop
      >
        {location?.pathname === RebookUrls.GET_BOOKING || location?.pathname === RebookUrls.CONFIRM
          ? <>
              <SVVHeading level="h1">
                {bannerHeading}
              </SVVHeading>
              <SVVIngress marginBottom="none">
                {bannerIngress}
              </SVVIngress>
            </>
          : <TjenesteHeading
              headingNokkel="timebestilling.handling.bestill"
              tjeneste={service}
            /> 
        }
      </SVVLayoutBanner>
      <SVVLayoutPageWrapper>
        <SVVLayoutPageType medium>
          <div className="form-layout">
            {location.pathname !== RebookUrls.GET_BOOKING && location.pathname !== RebookUrls.CONFIRM &&
              <div className="form-layout__nav">
                <SVVStepIndicator
                  routes={trimStegviser(routes, navState, window.location.pathname)}
                  ariaLabel={t("timebestilling.navigasjon")}
                />
              </div>
            }
            <div className="form-layout__main">
              <Routes>
                <Route
                  path="endretime"
                  element={
                    <GetBooking
                      isRebook={true}
                      setBooking={setOriginalBooking}
                    />
                  }
                />
                <Route
                  path="bekrefte"
                  element={
                    <RebookPreview
                      booking={originalBooking}
                      setNavState={() => {}}
                      abort={abort}
                      nesteUrl={visVelgTjeneste ? "/endre/tjeneste" : "/endre/trafikkstasjon"}
                    />
                  }
                />
                {visVelgTjeneste &&
                  <Route
                    path="tjeneste"
                    element={
                      <Service
                        visKunTeori
                        service={service}
                        setService={setService}
                        abort={abort}
                        nextUrl="/endre/trafikkstasjon"
                        avbrytUrl={avbrytUrl}
                      />
                    }
                  />
                }
                <Route
                  path="trafikkstasjon"
                  element={
                    <Section
                      section={section}
                      setSection={setSection}
                      service={service}
                      setNavState={() => {setNavState(navState + 1)}}
                      abort={abort}
                      nextUrl="/endre/tidspunkt"
                      prevUrl={visVelgTjeneste ? "/endre/tjeneste" : "/endre/bekrefte" }
                      resetBooking={resetBookingSectionAndDateTime}
                      avbrytUrl={avbrytUrl}
                    />
                  }
                />
                <Route
                  path="tidspunkt"
                  element={
                    <DateAndTime
                      setDateAndTime={(dateAndTime: string) => {
                        setDateAndTime(dateAndTime)
                      }}
                      dateAndTime={dateAndTime}
                      serviceId={service.serviceId}
                      sectionId={section.sectionId}
                      isTungKjoreToy={service.isTungKjoreToy}
                      setBooking={(booking: InterfaceBooking) => {
                        setNewBooking(booking)
                        setNavState(routes.findIndex((r) => r.href === RebookUrls.CONTACT))
                      }}
                      setReserved={setReserved}
                      setNavState={() => {}}
                      reserved={reserved}
                      bookingId={originalBooking.bookingId}
                      resetBooking={() => {
                        resetBookingIdAndDateTime()
                      }}
                      abort={abort}
                      nextUrl="/endre/kontakt"
                      prevUrl="/endre/trafikkstasjon"
                      avbrytUrl={avbrytUrl}
                    />
                  }
                />
                <Route
                  path="kontakt"
                  element={
                    <ContactInfo
                      originalBooking={originalBooking}
                      newBooking={newBooking}
                      setContactInfo={setContactInfo}
                      contactInfo={contactInfo}
                      setNavState={() => {setNavState(navState + 1)}}
                      abort={abort}
                      setSendReminderTo={setSendReminderTo}
                      backUrl={"/endre/tidspunkt"}
                      avbrytUrl={avbrytUrl}
                    />
                  }
                />{" "}
                <Route
                  path="bekreftetime"
                  element={
                    <Confirmation
                      originalBooking={originalBooking}
                      setBooking={setOriginalBooking}
                      newBooking={newBooking}
                      setNewBooking={setNewBooking}
                      contactInfo={contactInfo}
                      dateAndTime={dateAndTime}
                      setNavState={() => {}}
                      abort={abort}
                      confirmText={t("timebestilling.bekreft")}
                      sendReminderTo={sendReminderTo}
                      avbrytUrl={avbrytUrl}
                    />
                  }
                />
                <Route
                  path="kvittering"
                  element={
                    <Receipt
                      isRebook={true}
                      booking={newBooking}
                      sendReminderTo={sendReminderTo}
                    />
                  }
                />
              </Routes>
            </div>
          </div>
        </SVVLayoutPageType>
      </SVVLayoutPageWrapper>
    </>
  );
};

export default Rebook;
